import '../style.scss';
import { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import 'react-phone-input-2/lib/style.css';
import { IEditReportState, ISuccessFactorsTab, IMetadataModalityTab, ISuccessFactorsPolicy, ISuccessFactorsPlanningMethodology, ISuccessFactorsMonitoringSystem } from '../models/IEditReportState';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import Select, { MultiValue } from 'react-select';
import Accordion from 'react-bootstrap/Accordion';
import ConfigService from '../../../../shared/configService';
import { Modal } from 'react-bootstrap';
import { IFeature } from '@esri/arcgis-rest-types';
import QueryService from '../../../../shared/queryService';
import { IRefTierFeature, IRefPlanningStatusFeature, IRefPlanningObservScalesFeature, IRefPlanningApproachScaleFeature, IRefMonitoringApproachScaleFeature, IRefMonitoringTrackedElemFeature } from '../models/IRefFeature';
import Utils from '../../../../shared/utils';

const SuccessFactors = (props: {state: IEditReportState, setState: Function, validateData: Function}) => {
  const {t} = useTranslation('reports');

  // State
  interface ILocalState {
    reportLayerIndex: number;
    reportPoliciesLayerIndex: number;
    reportFinancialFlowLayerIndex: number;
    reportTechnicalPlanningLayerIndex: number;
    reportMonitoringLayerIndex: number;
    otherStatusInputEnabled: boolean;
    otherSystemTrackEnabled: boolean;
    currentDeletePolicyIndex: number;
    currentDeletePlanningMethodologyIndex: number;
    currentDeleteMonitoringSystemIndex: number;
    tiers: IRefTierFeature[];
    planningStatusFeatures: IRefPlanningStatusFeature[],
    planningObservScalesFeatures: IRefPlanningObservScalesFeature[],
    planningApproachScaleFeatures: IRefPlanningApproachScaleFeature[],
    monitoringTrackedElemFeatures: IRefMonitoringTrackedElemFeature[],
    monitoringGeoScaleFeatures: IRefMonitoringApproachScaleFeature[],
    policiesAndInstitTab: {
      isActive: boolean;
      isDisabled: boolean;
    },
    financialFlowsTab: {
      isActive: boolean;
      isDisabled: boolean;
    },
    technicalPlanningTab: {
      isActive: boolean;
      isDisabled: boolean;
    },
    monitoringTab: {
      isActive: boolean;
      isDisabled: boolean;
    }
  }

  const [localState, setTheLocalState] = useState<ILocalState>({
    reportLayerIndex: ConfigService.getConfig().agol?.operationalLayers.reportMain.index,
    reportPoliciesLayerIndex: ConfigService.getConfig().agol?.operationalLayers.reportPolicies.index,
    reportFinancialFlowLayerIndex: ConfigService.getConfig().agol?.operationalLayers.reportFinancialFlows.index,
    reportTechnicalPlanningLayerIndex: ConfigService.getConfig().agol?.operationalLayers.reportTechnicalPlanning.index,
    reportMonitoringLayerIndex: ConfigService.getConfig().agol?.operationalLayers.reportMonitoring.index,
    otherStatusInputEnabled: false,
    otherSystemTrackEnabled: false,
    currentDeletePolicyIndex: -1,
    currentDeletePlanningMethodologyIndex: -1,
    currentDeleteMonitoringSystemIndex: -1,
    tiers: [],
    planningStatusFeatures: [],
    planningObservScalesFeatures: [],
    planningApproachScaleFeatures: [],
    monitoringTrackedElemFeatures: [],
    monitoringGeoScaleFeatures: [],
    policiesAndInstitTab: {
      isActive: false,
      isDisabled: false
    },
    financialFlowsTab: {
      isActive: false,
      isDisabled: false
    },
    technicalPlanningTab: {
      isActive: false,
      isDisabled: false
    },
    monitoringTab: {
      isActive: false,
      isDisabled: false
    }
  });

  // Confirm dialog
  const [showPolicyDialog, setPolicyDialogShow] = useState(false);
  const [showPlanningMethodologyDialog, setPlanningMethodologyDialogShow] = useState(false);
  const [showMonitoringSystemDialog, setMonitoringSystemDialogShow] = useState(false);

  // Use effect
  useEffect(() => {  
    setFinancialFlowsIntro(); 
    getRefFeatures();
  }, []);

  useEffect(() => {
    enableDisableTabs();
  }, [props.state.metadataModalityTab?.report_type, 
    props.state.metadataModalityTab?.successFactorFinancialFlowOn,
    props.state.metadataModalityTab?.successFactorMonitoringOn,
    props.state.metadataModalityTab?.successFactorPoliciesInstitOn,
    props.state.metadataModalityTab?.successFactorTechPlanningOn]
  );
  
  //Set financial flow Intro
  const setFinancialFlowsIntro = () => {
    const financialIntro: HTMLSpanElement | null = document.getElementById('financialIntro');
    
    if(financialIntro) {
      financialIntro.innerHTML = t('newReport.successFactors.financialFlowsIntro');
    }
  }

  /**
   * Get reference list
   */
   const getRefFeatures = async () => {

    // Ref data
    const countryFilter = "reporting_entity_type = '1' OR reporting_entity_type = '1,2'";
    //const tierFeatures: IFeature[] = await QueryService.queryReferenceFeatures(ConfigService.getConfig().agol?.referenceTables.REF_TIERS.index);
    const tierFeatures: IFeature[] = await QueryService.queryFilterReferenceFeatures(Utils.getReferenceTableQuery(ConfigService.getConfig().agol.referenceTables.REF_TIERS.index), countryFilter, false, '*','');
    const planningStatusFeatures: IFeature[] = await QueryService.queryReferenceFeatures(ConfigService.getConfig().agol?.referenceTables.REF_PLANNING_STATUS.index);
    const planningObservScalesFeatures: IFeature[] = await QueryService.queryReferenceFeatures(ConfigService.getConfig().agol?.referenceTables.REF_PLANNING_OBSERVATION_SCALE.index);
    const planningApproachScaleFeatures: IFeature[] = await QueryService.queryReferenceFeatures(ConfigService.getConfig().agol?.referenceTables.REF_PLANNING_APPROACH_SCALE.index);
    //const monitoringTrackedElemFeatures: IFeature[] = await QueryService.queryReferenceFeatures(ConfigService.getConfig().agol?.referenceTables.REF_MONITORING_TRACKED_ELEMENTS.index);
    const monitoringTrackedElemFeatures: IFeature[] = await QueryService.queryFilterReferenceFeatures(Utils.getReferenceTableQuery(ConfigService.getConfig().agol.referenceTables.REF_MONITORING_TRACKED_ELEMENTS.index), countryFilter, false, '*','');
    const monitoringGeoScaleFeatures: IFeature[] = await QueryService.queryReferenceFeatures(ConfigService.getConfig().agol?.referenceTables.REF_MONITORING_APPROACH_SCALE.index);

    const obj: ILocalState = { ...localState };
    obj.tiers = tierFeatures.map((feat: IFeature) => feat.attributes as IRefTierFeature);    
    obj.planningStatusFeatures = planningStatusFeatures.map((feat: IFeature) => feat.attributes as IRefPlanningStatusFeature);
    obj.planningObservScalesFeatures = planningObservScalesFeatures.map((feat: IFeature) => feat.attributes as IRefPlanningObservScalesFeature);
    obj.planningApproachScaleFeatures = planningApproachScaleFeatures.map((feat: IFeature) => feat.attributes as IRefPlanningApproachScaleFeature);
    obj.monitoringTrackedElemFeatures = monitoringTrackedElemFeatures.map((feat: IFeature) => feat.attributes as IRefMonitoringTrackedElemFeature);
    obj.monitoringGeoScaleFeatures = monitoringGeoScaleFeatures.map((feat: IFeature) => feat.attributes as IRefMonitoringApproachScaleFeature);

    setTheLocalState(obj);

    // Get layer infos
    await QueryService.getLayerFieldsInfos(localState.reportPoliciesLayerIndex);
    await QueryService.getLayerFieldsInfos(localState.reportFinancialFlowLayerIndex);
    await QueryService.getLayerFieldsInfos(localState.reportTechnicalPlanningLayerIndex);
    await QueryService.getLayerFieldsInfos(localState.reportMonitoringLayerIndex);

    // Validate data
    callSetState({...props.state});
  };

  /**
   * Call set state (with validation)
   * @param obj 
   */
   const callSetState = (obj: IEditReportState) => {
    
    const sfTab: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;
    const metaTab: IMetadataModalityTab = { ...obj.metadataModalityTab } as IMetadataModalityTab;
    
    if(sfTab && metaTab) {
      props.validateData(sfTab, metaTab);
    }

    obj.successFactorsTab = sfTab;
    props.setState(obj);
  }

  /**
   * Set financial flows state
   * @param tabIndex
   * @param prop
   * @param value 
   */
   const setFinancialFlowsState = (tabIndex: number, prop: string, value: any) => {

    if(props.state) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;
      
      switch (tabIndex) {
        case 0:
          (subObj.financialFlowsTab.totalDomesticPublicExpenditure as any)[prop] = value;
          subObj.financialFlowsTab.totalDomesticPublicExpenditure!.report_id = obj.report?.attributes[ConfigService.getConfig().agol.operationalLayers.reportMain.fields.reportID];
        break;

        case 1:
          (subObj.financialFlowsTab.totalPrivateInvestments as any)[prop] = value;
          subObj.financialFlowsTab.totalPrivateInvestments!.report_id = obj.report?.attributes[ConfigService.getConfig().agol.operationalLayers.reportMain.fields.reportID];
        break;

        case 2:
          (subObj.financialFlowsTab.totalInternationalDonorSupport as any)[prop] = value;
          subObj.financialFlowsTab.totalInternationalDonorSupport!.report_id = obj.report?.attributes[ConfigService.getConfig().agol.operationalLayers.reportMain.fields.reportID];
        break;

        case 3:
          (subObj.financialFlowsTab.totalDomesticPhilanthropicAndNonProfit as any)[prop] = value;
          subObj.financialFlowsTab.totalDomesticPhilanthropicAndNonProfit!.report_id = obj.report?.attributes[ConfigService.getConfig().agol.operationalLayers.reportMain.fields.reportID];
        break;
      }

      callSetState(obj);
    }
  }

  /**
   * Select tab
   * @param index 
   */
  const selectTab = (index: number) => {
    const obj: ILocalState = { ...localState };
    
    obj.policiesAndInstitTab.isActive = index === 0;
    obj.financialFlowsTab.isActive = index === 1;
    obj.technicalPlanningTab.isActive = index === 2;
    obj.monitoringTab.isActive = index === 3;    
    
    setTheLocalState(obj);   
  }


  /**
   * Enable disable tab
   * @param index 
   */
   const enableDisableTabs = () => {
    const obj: ILocalState = { ...localState };

    obj.policiesAndInstitTab.isDisabled = props.state.metadataModalityTab?.report_type === ConfigService.getConfig().agol.referenceTables.REF_MODALITY.snapshotValue && !props.state.metadataModalityTab?.successFactorPoliciesInstitOn;
    obj.financialFlowsTab.isDisabled = props.state.metadataModalityTab?.report_type === ConfigService.getConfig().agol.referenceTables.REF_MODALITY.snapshotValue && !props.state.metadataModalityTab?.successFactorFinancialFlowOn;
    obj.technicalPlanningTab.isDisabled = props.state.metadataModalityTab?.report_type === ConfigService.getConfig().agol.referenceTables.REF_MODALITY.snapshotValue && !props.state.metadataModalityTab?.successFactorTechPlanningOn;
    obj.monitoringTab.isDisabled = props.state.metadataModalityTab?.report_type === ConfigService.getConfig().agol.referenceTables.REF_MODALITY.snapshotValue && !props.state.metadataModalityTab?.successFactorMonitoringOn;

    if(!obj.policiesAndInstitTab.isDisabled) {
      obj.policiesAndInstitTab.isActive = true;
    } else  if(!obj.financialFlowsTab.isDisabled) {
      obj.financialFlowsTab.isActive = true;
    } else  if(!obj.technicalPlanningTab.isDisabled) {
      obj.technicalPlanningTab.isActive = true;
    } else  if(!obj.monitoringTab.isDisabled) {
      obj.monitoringTab.isActive = true;
    }

    setTheLocalState(obj);   
  }


  /**
   * Add new policy
   */
  const addNewPolicy = () => {
    if(props.state) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;

      if (subObj.policiesAndInstitTab.policies.length === 10) {
        return;
      }
      const policy: ISuccessFactorsPolicy = {
        isActive: true,
        name: t('newReport.successFactors.defaultPolicyName'),
        OBJECTID: -1,
        report_id: props.state.report?.attributes[ConfigService.getConfig().agol.operationalLayers.reportMain.fields.reportID]
      }
      
      // Add new policy
      subObj.policiesAndInstitTab.policies.unshift(policy);

      subObj.policiesAndInstitTab.policies.forEach( (p: ISuccessFactorsPolicy, i: number) => {
        p.isActive = i === 0;
      });

      callSetState(obj);
    }
  }

  /**
   * Delete policy
   * @param event 
   * @param index 
   */
  const deletePolicy = (event: any, index: number) => {
    event.stopPropagation();
    setCurrentPolicyIndex(index);
    handlePolicyDialogShow();
  }

  /**
   * Select policy
   * @param index 
   */
  const selectPolicy = (index: number) => {
    if(props.state) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;

      subObj.policiesAndInstitTab.policies.forEach( (p: ISuccessFactorsPolicy, i: number) => {
        p.isActive = i === index;
      });
      
      callSetState(obj);
    }
  }

  /**
   * Update selected policy
   * @param prop
   * @param value 
   */
  const updateSelectedPolicy = (prop: string, value: any) => {
    if(props.state) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;

      const policy: ISuccessFactorsPolicy | undefined = subObj.policiesAndInstitTab.policies.find( (p: ISuccessFactorsPolicy, i: number) => p.isActive);
      
      if(policy) {
        (policy as any)[prop] = value;
        policy.report_id = obj.report?.attributes[ConfigService.getConfig().agol.operationalLayers.reportMain.fields.reportID];
        callSetState(obj);
      }
    }
  }

  /**
   * Get selected policy
   * @returns 
   */
  const getSelectedPolicy = (): ISuccessFactorsPolicy | undefined  => {
    if(props.state) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;

      return subObj.policiesAndInstitTab.policies.find( (p: ISuccessFactorsPolicy, i: number) => p.isActive);
    }
    
    return undefined;
  }

  /**
   * Set current policy index
   * @param i
   */
  const setCurrentPolicyIndex = (i: number) => {
    const obj: ILocalState = { ...localState };
    obj.currentDeletePolicyIndex = i;
    setTheLocalState(obj);
  }

  /**
   * Add new PlanningMethodology
   */
  const addNewPlanningMethodology = () => {
    if(props.state) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;

      if (subObj.technicalPlanningTab.planningMethodologies.length === 10) {
        return;
      }
      const planningMethodology: ISuccessFactorsPlanningMethodology = {
        isActive: true,
        methodology: t('newReport.successFactors.defaultPlanningMethodology'),
        OBJECTID: -1,
        report_id: props.state.report?.attributes[ConfigService.getConfig().agol.operationalLayers.reportMain.fields.reportID]
      }
      
      // Add new PlanningMethodology
      subObj.technicalPlanningTab.planningMethodologies.unshift(planningMethodology);

      subObj.technicalPlanningTab.planningMethodologies.forEach( (p: ISuccessFactorsPlanningMethodology, i: number) => {
        p.isActive = i === 0;
      });

      callSetState(obj);
    }
  }

  /**
   * Delete PlanningMethodology
   * @param event 
   * @param index 
   */
  const deletePlanningMethodology = (event: any, index: number) => {
    event.stopPropagation();
    setCurrentPlanningMethodologyIndex(index);
    handlePlanningMethodologyDialogShow();
  }

  /**
   * Select PlanningMethodology
   * @param index 
   */
  const selectPlanningMethodology = (index: number) => {
    if(props.state) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;

      subObj.technicalPlanningTab.planningMethodologies.forEach( (p: ISuccessFactorsPlanningMethodology, i: number) => {
        p.isActive = i === index;
      });
      
      callSetState(obj);
    }
  }

  /**
   * Update selected PlanningMethodology
   * @param prop
   * @param value 
   */
  const updateSelectedPlanningMethodology = (prop: string, value: any) => {
    if(props.state) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;

      const planningMethodology: ISuccessFactorsPlanningMethodology | undefined = subObj.technicalPlanningTab.planningMethodologies.find( (p: ISuccessFactorsPlanningMethodology, i: number) => p.isActive);
      
      if(planningMethodology) {
        (planningMethodology as any)[prop] = value;
        callSetState(obj);
      }
    }
  }

  /**
   * Get selected PlanningMethodology
   * @returns 
   */
  const getSelectedPlanningMethodology = (): ISuccessFactorsPlanningMethodology | undefined  => {
    if(props.state) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;

      return subObj.technicalPlanningTab.planningMethodologies.find( (p: ISuccessFactorsPlanningMethodology, i: number) => p.isActive);
    }
    
    return undefined;
  }

  /**
   * Set current PlanningMethodology index
   * @param i
   */
   const setCurrentPlanningMethodologyIndex = (i: number) => {
    const obj: ILocalState = { ...localState };
    obj.currentDeletePlanningMethodologyIndex = i;
    setTheLocalState(obj);
  }

  /**
   * Handle planning status change
   * @param date
   */
   const handlePlanningStatusChange = (items: MultiValue<{label: string | undefined, value: string | undefined}>) => {
    updateSelectedPlanningMethodology('landscape_status', items && items.length ? items.map(i => i.value).join(',') : '');

    const obj: ILocalState = { ...localState };
    obj.otherStatusInputEnabled = items && items.some((i: any) => i.value === ConfigService.getConfig().agol.referenceTables.otherKeyValue);
    setTheLocalState(obj);
  }

  /**
   * Add new MonitoringSystem
   */
  const addNewMonitoringSystem = () => {
    if(props.state) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;

      if (subObj.monitoringTab.monitoringSystems.length === 10) {
        return;
      }
      const monitoringSystem: ISuccessFactorsMonitoringSystem = {
        isActive: true,
        monitoring_system: t('newReport.successFactors.defaultMonitoringSystem'),
        OBJECTID: -1,
        report_id: props.state.report?.attributes[ConfigService.getConfig().agol.operationalLayers.reportMain.fields.reportID]
      }
      
      // Add new MonitoringSystem
      subObj.monitoringTab.monitoringSystems.unshift(monitoringSystem);

      subObj.monitoringTab.monitoringSystems.forEach( (p: ISuccessFactorsMonitoringSystem, i: number) => {
        p.isActive = i === 0;
      });

      callSetState(obj);
    }
  }

  /**
   * Delete MonitoringSystem
   * @param event 
   * @param index 
   */
  const deleteMonitoringSystem = (event: any, index: number) => {
    event.stopPropagation();
    setCurrentMonitoringSystemIndex(index);
    handleMonitoringSystemDialogShow();
  }

  /**
   * Select MonitoringSystem
   * @param index 
   */
  const selectMonitoringSystem = (index: number) => {
    if(props.state) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;

      subObj.monitoringTab.monitoringSystems.forEach( (p: ISuccessFactorsMonitoringSystem, i: number) => {
        p.isActive = i === index;
      });
      
      callSetState(obj);
    }
  }

  /**
   * Update selected MonitoringSystem
   * @param prop
   * @param value 
   */
  const updateSelectedMonitoringSystem = (prop: string, value: any) => {
    if(props.state) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;

      const monitoringSystem: ISuccessFactorsMonitoringSystem | undefined = subObj.monitoringTab.monitoringSystems.find( (p: ISuccessFactorsMonitoringSystem, i: number) => p.isActive);
      
      if(monitoringSystem) {
        (monitoringSystem as any)[prop] = value;
        callSetState(obj);
      }
    }
  }

  /**
   * Get selected MonitoringSystem
   * @returns 
   */
  const getSelectedMonitoringSystem = (): ISuccessFactorsMonitoringSystem | undefined  => {
    if(props.state) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;

      return subObj.monitoringTab.monitoringSystems.find( (p: ISuccessFactorsMonitoringSystem, i: number) => p.isActive);
    }
    
    return undefined;
  }

  /**
   * Set current MonitoringSystem index
   * @param i
   */
   const setCurrentMonitoringSystemIndex = (i: number) => {
    const obj: ILocalState = { ...localState };
    obj.currentDeleteMonitoringSystemIndex = i;
    setTheLocalState(obj);
  }

  /**
   * Handle planning status change
   * @param date
   */
   const handleSystemTrackChange = (items: MultiValue<{label: string | undefined, value: string | undefined}>) => {
    updateSelectedMonitoringSystem('track_list', items && items.length ? items.map(i => i.value).join(',') : '');

    const obj: ILocalState = { ...localState };
    obj.otherSystemTrackEnabled = items && items.some((i: any) => i.value === ConfigService.getConfig().agol.referenceTables.otherKeyValue);
    setTheLocalState(obj);
  }

  /**
   * Handle policy dialog
   */
  const handlePolicyDialogClose = () => setPolicyDialogShow(false);
  const handlePolicyDialogShow = () => setPolicyDialogShow(true);
  const handlePolicyConfirmDelete = () => {
    if(props.state && localState.currentDeletePolicyIndex > -1) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;
      subObj.policiesAndInstitTab.policies.splice(localState.currentDeletePolicyIndex, 1);      
      callSetState(obj);
    }

    setCurrentPolicyIndex(-1);
    handlePolicyDialogClose();
  }

  /**
   * Handle PlanningMethodology dialog
   */
  const handlePlanningMethodologyDialogClose = () => setPlanningMethodologyDialogShow(false);
  const handlePlanningMethodologyDialogShow = () => setPlanningMethodologyDialogShow(true);
  const handlePlanningMethodologyConfirmDelete = () => {
  if(props.state && localState.currentDeletePlanningMethodologyIndex > -1) {
    const obj: IEditReportState = { ...props.state } as IEditReportState;
    const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;
    subObj.technicalPlanningTab.planningMethodologies.splice(localState.currentDeletePlanningMethodologyIndex, 1);      
    callSetState(obj);
  }

  setCurrentPlanningMethodologyIndex(-1);
  handlePlanningMethodologyDialogClose();
  }

  /**
   * Handle MonitoringSystem dialog
   */
  const handleMonitoringSystemDialogClose = () => setMonitoringSystemDialogShow(false);
  const handleMonitoringSystemDialogShow = () => setMonitoringSystemDialogShow(true);
  const handleMonitoringSystemConfirmDelete = () => {
    if(props.state && localState.currentDeleteMonitoringSystemIndex > -1) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;
      subObj.monitoringTab.monitoringSystems.splice(localState.currentDeleteMonitoringSystemIndex, 1);      
      callSetState(obj);
    }

    setCurrentMonitoringSystemIndex(-1);
    handleMonitoringSystemDialogClose();
  }

  /**
   * Check if a field is required in policy tab
   * @param field
   */
  const isRequiredPolicy = (field: string): boolean => {
    return Utils.isRequiredField(ConfigService.getConfig().agol.operationalLayers.reportPolicies.requiredFields, field);
  }

  /**
   * Check if a field is required in financial flow tab
   * @param field
   */
   const isRequiredFinFlow = (field: string): boolean => {
    return Utils.isRequiredField(ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.requiredFields, field);
  }

  /**
   * Check if a field is required in technical planning tab
   * @param field
   */
   const isRequiredTechPlan = (field: string): boolean => {
    return Utils.isRequiredField(ConfigService.getConfig().agol.operationalLayers.reportTechnicalPlanning.requiredFields, field);
  }

  /**
   * Check if a field is required in monitoring tab
   * @param field
   */
   const isRequiredMonitoring = (field: string): boolean => {
    return Utils.isRequiredField(ConfigService.getConfig().agol.operationalLayers.reportMonitoring.requiredFields, field);
  }
   
  return(
    <div className="oneTabContent successFactorsTab">

      <div className="successFactorsTabs">
        <div className={`oneTabItem ${localState.policiesAndInstitTab.isActive ? 'active' : localState.policiesAndInstitTab.isDisabled ? 'disabled default' : 'default'}`} onClick={() => selectTab(0)}>{'1. ' + t('newReport.successFactors.policiesAndInstitLabel')}</div>
        <div className={`oneTabItem ${localState.financialFlowsTab.isActive ? 'active' : localState.financialFlowsTab.isDisabled ? 'disabled default' : 'default'}`} onClick={() => selectTab(1)}>{'2. ' + t('newReport.successFactors.financialFlowsLabel')}</div>
        <div className={`oneTabItem ${localState.technicalPlanningTab.isActive ? 'active' : localState.technicalPlanningTab.isDisabled ? 'disabled default' : 'default'}`} onClick={() => selectTab(2)}>{'3. ' + t('newReport.successFactors.technicalPlanningLabel')}</div>
        <div className={`oneTabItem ${localState.monitoringTab.isActive ? 'active' : localState.monitoringTab.isDisabled ? 'disabled default' : 'default'}`} onClick={() => selectTab(3)}>{'4. ' + t('newReport.successFactors.monitoringLabel')}</div>
      </div>
      
      <div className="successFactorsContent">
        <div className="borderHider">
          <div className={`oneBorderHider ${localState.policiesAndInstitTab.isActive ? 'active' : ''}`}></div>
          <div className={`oneBorderHider ${localState.financialFlowsTab.isActive ? 'active' : ''}`}></div>
          <div className={`oneBorderHider ${localState.technicalPlanningTab.isActive ? 'active' : ''}`}></div>
          <div className={`oneBorderHider ${localState.monitoringTab.isActive ? 'active' : ''}`}></div>
        </div>
        
        { /* policiesAndInstitTab */ }
        <div className={`oneTabContent ${!localState.policiesAndInstitTab.isActive && 'hidden'}`}>
          <div className="oneTabIntro">
            <div className="oneTabIntroNumAndTitle">
              <span className="oneTabIntroNumber">1</span>
              <span className="oneTabIntroTitle">{t('newReport.successFactors.policiesAndInstitLabel')}</span>              
            </div>
            
            <p className="oneTabIntroText">{t('newReport.successFactors.policiesAndInstitIntro')}</p>
          </div>

          <div className="itemsDiv row">
            <div className="itemsLeft">
              <div className="addItemDiv">
                <button className="actionBtn primary" onClick={addNewPolicy}>
                    <span>{t('newReport.successFactors.addNewPolicy')}</span>
                    <FontAwesomeIcon className="plusIcon" icon={ faPlus }/>
                </button>   
              </div>

              <div className="itemsList">

              {props.state.successFactorsTab?.policiesAndInstitTab.policies.map((p:ISuccessFactorsPolicy, i: number) =>
                <div className={`oneItem ${p.isActive ? 'active' : 'default'}`} key={i} onClick={ () => {selectPolicy(i)}}>
                  <span className="tabSpan" title={p.name}>{p.name}</span>
                  <FontAwesomeIcon className="tabIcon deleteIcon" icon={ faTrash } onClick={ (e) => { deletePolicy(e, i) }}/>
                </div>
              )}
              </div>
            </div>

            {getSelectedPolicy() &&
            <div className="itemsRight">
              <div className="onePolicyRow row">{}
                <span className="titleSpan mediumText">{t('newReport.successFactors.policyName')}</span>
                <p><input className="commonText medium" required = {isRequiredPolicy('name')}
                maxLength={Utils.getFieldInfos(localState.reportPoliciesLayerIndex, 'name')?.length || 9999}
                type="text" placeholder={t('newReport.successFactors.policyNamePlaceholder')} value={getSelectedPolicy()?.name || ''}  onChange={(e) => {updateSelectedPolicy('name', e.target.value)}}/> </p>
              </div>

              <div className="onePolicyRow row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.startYear')}</span>
                <p><input className="commonText verySmall" type="number" required = {isRequiredPolicy('year_enacted')}
                maxLength={Utils.getFieldInfos(localState.reportPoliciesLayerIndex, 'year_enacted')?.length || 9999} min={1900} max={2100} onKeyUp={Utils.validateYear}
                placeholder={t('newReport.successFactors.startYearPlaceholder')} value={getSelectedPolicy()?.year_enacted || ''} onChange={(e) => {updateSelectedPolicy('year_enacted', Number(e.target.value))}}/> </p>
              </div>

              <div className="onePolicyRow row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.endYear')}</span>
                <p><input className="commonText verySmall" type="number" required = {isRequiredPolicy('year_editing')}
                maxLength={Utils.getFieldInfos(localState.reportPoliciesLayerIndex, 'year_editing')?.length || 9999} min={1900} max={2100} onKeyUp={Utils.validateYear}
                placeholder={t('newReport.successFactors.endYearPlaceholder')} value={getSelectedPolicy()?.year_editing || ''} onChange={(e) => {updateSelectedPolicy('year_editing', Number(e.target.value))}}/> </p>
              </div>

              <div className="onePolicyRow row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.hectaresNumber')}</span>
                <p><input className="commonText verySmall" type="number" required = {isRequiredPolicy('ha_targeted')}
                maxLength={Utils.getFieldInfos(localState.reportPoliciesLayerIndex, 'ha_targeted')?.length || 9999} min={0} onKeyUp={Utils.preventNegativeValue}
                placeholder={t('newReport.successFactors.hectaresNumberPlaceholder')} value={getSelectedPolicy()?.ha_targeted || ''} onChange={(e) => {updateSelectedPolicy('ha_targeted', Number(e.target.value))}}/> </p>
              </div>

              <div className="onePolicyRow row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.budgetaryAllocations')}</span>
                <p><input className="commonText verySmall" type="number" required = {isRequiredPolicy('budget_alloc')}
                maxLength={Utils.getFieldInfos(localState.reportPoliciesLayerIndex, 'budget_alloc')?.length || 9999} min={0} onKeyUp={Utils.preventNegativeValue}
                placeholder={t('newReport.successFactors.budgetaryAllocationsPlaceholder')} value={getSelectedPolicy()?.budget_alloc || ''} onChange={(e) => {updateSelectedPolicy('budget_alloc', Number(e.target.value))}}/> </p>
              </div>

              <div className="onePolicyRow row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.governmentMinistry')}</span>
                <p><input className="commonText medium" type="text" required = {isRequiredPolicy('responsible')}
                maxLength={Utils.getFieldInfos(localState.reportPoliciesLayerIndex, 'responsible')?.length || 9999}
                placeholder={t('newReport.successFactors.governmentMinistryPlaceholder')} value={getSelectedPolicy()?.responsible || ''} onChange={(e) => {updateSelectedPolicy('responsible', e.target.value)}}/> </p>
              </div>

              <div className="onePolicyRow row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.policiesCitations')}</span>
                <p><textarea className="commonText medium" required = {isRequiredPolicy('links')}
                maxLength={Utils.getFieldInfos(localState.reportPoliciesLayerIndex, 'links')?.length || 9999}
                rows={5} placeholder={t('newReport.successFactors.citationsPlaceholder')} value={getSelectedPolicy()?.links || ''} onChange={(e) => {updateSelectedPolicy('links', e.target.value)}}/> </p>
              </div>

              <div className="onePolicyRow row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.otherInfo')}</span>
                <p><textarea className="commonText medium" required = {isRequiredPolicy('information')}
                maxLength={Utils.getFieldInfos(localState.reportPoliciesLayerIndex, 'information')?.length || 9999}
                rows={5} placeholder={t('newReport.successFactors.otherInfoPlaceholder')} value={getSelectedPolicy()?.information || ''} onChange={(e) => {updateSelectedPolicy('information', e.target.value)}}/> </p>
              </div>

            </div>
            }
          </div>
        </div>

        { /* financialFlowsTab */ }
        <div className={`oneTabContent ${!localState.financialFlowsTab.isActive && 'hidden'}`}>
          <div className="oneTabIntro">
            <div className="oneTabIntroNumAndTitle">
              <span className="oneTabIntroNumber">2</span>
              <span className="oneTabIntroTitle">{t('newReport.successFactors.financialFlowsLabel')}</span>              
            </div>
            
            <p className="oneTabIntroText" id="financialIntro"></p>
          </div>

          <div className='flowsDiv row'>
          <Accordion>
            {/** Total domestic public expenditure*/}
            <Accordion.Item eventKey="0">
              <Accordion.Header>{t('newReport.successFactors.financialFlowsHeaderLabel1')}</Accordion.Header>
              <Accordion.Body>
              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.amount')}</span>                       
                <input className="mediumText small" type="number" required = {isRequiredFinFlow('amount')} 
                min={0} onKeyUp={Utils.preventNegativeValue}
                placeholder={t('newReport.successFactors.amountPlaceholder')} value={props.state.successFactorsTab?.financialFlowsTab.totalDomesticPublicExpenditure?.amount || 0} onChange={(e) => {setFinancialFlowsState(0, 'amount', e.target.value)}}/>  
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.benefitsPercentage')}</span>                       
                <input className="mediumText small" type="number" required = {isRequiredFinFlow('critical_species_fund_percent')}
                min={0} onKeyUp={Utils.preventNegativeValue}
                placeholder={t('newReport.successFactors.benefitsPercentagePlaceholder')} value={props.state.successFactorsTab?.financialFlowsTab.totalDomesticPublicExpenditure?.critical_species_fund_percent || ''} onChange={(e) => {setFinancialFlowsState(0, 'critical_species_fund_percent', e.target.value)}}/>  
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.tier')}</span>
                <select className={`mediumText small ${isRequiredFinFlow('tier') ? "required": ""}`}
                value={props.state.successFactorsTab?.financialFlowsTab.totalDomesticPublicExpenditure?.tier || '-999'}
                onChange={(e) => {setFinancialFlowsState(0, 'tier', e.target.value)}}>
                {<option value="-999" key="">{t('newReport.successFactors.selectOnePlacehodler')}</option>}
                {localState.tiers.map((i) => <option value={i.key_} key={i.key_}>{i.value}</option>)}
                </select>
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.source')}</span>
                <textarea className="commonText big" required = {isRequiredFinFlow('sources')}
                maxLength={Utils.getFieldInfos(localState.reportFinancialFlowLayerIndex, 'sources')?.length || 9999}
                rows={5} placeholder={t('newReport.successFactors.sourcePlaceholder')} value={props.state.successFactorsTab?.financialFlowsTab.totalDomesticPublicExpenditure?.sources || ''} onChange={(e) => {setFinancialFlowsState(0, 'sources', e.target.value)}}/>
              </div>
              
              </Accordion.Body>
            </Accordion.Item>

            {/** Total private investments*/}
            <Accordion.Item eventKey="1">
              <Accordion.Header>{t('newReport.successFactors.financialFlowsHeaderLabel2')}</Accordion.Header>
              <Accordion.Body>
              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.amount')}</span>                       
                <input className="mediumText small" type="number" required = {isRequiredFinFlow('amount')}
                min={0} onKeyUp={Utils.preventNegativeValue}
                placeholder={t('newReport.successFactors.amountPlaceholder')} value={props.state.successFactorsTab?.financialFlowsTab.totalPrivateInvestments?.amount || 0} onChange={(e) => {setFinancialFlowsState(1, 'amount', e.target.value)}}/>  
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.benefitsPercentage')}</span>                       
                <input className="mediumText small" type="number" required = {isRequiredFinFlow('critical_species_fund_percent')}
                min={0} onKeyUp={Utils.preventNegativeValue}
                placeholder={t('newReport.successFactors.benefitsPercentagePlaceholder')} value={props.state.successFactorsTab?.financialFlowsTab.totalPrivateInvestments?.critical_species_fund_percent || ''} onChange={(e) => {setFinancialFlowsState(1, 'critical_species_fund_percent', e.target.value)}}/>  
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.tier')}</span>
                <select className={`mediumText small ${isRequiredFinFlow('tier') ? "required": ""}`}
                value={props.state.successFactorsTab?.financialFlowsTab.totalPrivateInvestments?.tier || '-999'}
                onChange={(e) => {setFinancialFlowsState(1, 'tier', e.target.value)}}>
                {<option value="-999" key="">{t('newReport.successFactors.selectOnePlacehodler')}</option>}
                {localState.tiers.map((i) => <option value={i.key_} key={i.key_}>{i.value}</option>)}
                </select>
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.source')}</span>
                <textarea className="commonText big" required = {isRequiredFinFlow('sources')}
                maxLength={Utils.getFieldInfos(localState.reportFinancialFlowLayerIndex, 'sources')?.length || 9999}
                rows={5} placeholder={t('newReport.successFactors.sourcePlaceholder')} value={props.state.successFactorsTab?.financialFlowsTab.totalPrivateInvestments?.sources || ''} onChange={(e) => {setFinancialFlowsState(1, 'sources', e.target.value)}}/>
              </div>
              </Accordion.Body>
            </Accordion.Item>

             {/** Total international donor support*/}
            <Accordion.Item eventKey="2">
              <Accordion.Header>{t('newReport.successFactors.financialFlowsHeaderLabel3')}</Accordion.Header>
              <Accordion.Body>
              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.amount')}</span>                       
                <input className="mediumText small" type="number" required = {isRequiredFinFlow('amount')}
                min={0} onKeyUp={Utils.preventNegativeValue}
                placeholder={t('newReport.successFactors.amountPlaceholder')} value={props.state.successFactorsTab?.financialFlowsTab.totalInternationalDonorSupport?.amount || 0} onChange={(e) => {setFinancialFlowsState(2, 'amount', e.target.value)}}/>  
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.benefitsPercentage')}</span>                       
                <input className="mediumText small" type="number" required = {isRequiredFinFlow('critical_species_fund_percent')}
                min={0} onKeyUp={Utils.preventNegativeValue}
                placeholder={t('newReport.successFactors.benefitsPercentagePlaceholder')} value={props.state.successFactorsTab?.financialFlowsTab.totalInternationalDonorSupport?.critical_species_fund_percent || ''} onChange={(e) => {setFinancialFlowsState(2, 'critical_species_fund_percent', e.target.value)}}/>  
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.tier')}</span>
                <select className={`mediumText small ${isRequiredFinFlow('tier') ? "required": ""}`}
                value={props.state.successFactorsTab?.financialFlowsTab.totalInternationalDonorSupport?.tier || '-999'}
                onChange={(e) => {setFinancialFlowsState(2, 'tier', e.target.value)}}>
                {<option value="-999" key="">{t('newReport.successFactors.selectOnePlacehodler')}</option>}
                {localState.tiers.map((i) => <option value={i.key_} key={i.key_}>{i.value}</option>)}
                </select>
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.source')}</span>
                <textarea className="commonText big" required = {isRequiredFinFlow('sources')}
                maxLength={Utils.getFieldInfos(localState.reportFinancialFlowLayerIndex, 'sources')?.length || 9999} 
                rows={5} placeholder={t('newReport.successFactors.sourcePlaceholder')} value={props.state.successFactorsTab?.financialFlowsTab.totalInternationalDonorSupport?.sources || ''} onChange={(e) => {setFinancialFlowsState(2, 'sources', e.target.value)}}/>
              </div>
              </Accordion.Body>
            </Accordion.Item>

             {/** Total domestic philanthropic & non-profit*/}
            <Accordion.Item eventKey="3">
              <Accordion.Header>{t('newReport.successFactors.financialFlowsHeaderLabel4')}</Accordion.Header>
              <Accordion.Body>
              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.amount')}</span>                       
                <input className="mediumText small" type="number" required = {isRequiredFinFlow('amount')}
                min={0} onKeyUp={Utils.preventNegativeValue}
                placeholder={t('newReport.successFactors.amountPlaceholder')} value={props.state.successFactorsTab?.financialFlowsTab.totalDomesticPhilanthropicAndNonProfit?.amount || 0} onChange={(e) => {setFinancialFlowsState(3, 'amount', e.target.value)}}/>  
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.benefitsPercentage')}</span>                       
                <input className="mediumText small" type="number" required = {isRequiredFinFlow('critical_species_fund_percent')}
                min={0} onKeyUp={Utils.preventNegativeValue}
                placeholder={t('newReport.successFactors.benefitsPercentagePlaceholder')} value={props.state.successFactorsTab?.financialFlowsTab.totalDomesticPhilanthropicAndNonProfit?.critical_species_fund_percent || ''} onChange={(e) => {setFinancialFlowsState(3, 'critical_species_fund_percent', e.target.value)}}/>  
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.tier')}</span>
                <select className={`mediumText small ${isRequiredFinFlow('tier') ? "required": ""}`}
                value={props.state.successFactorsTab?.financialFlowsTab.totalDomesticPhilanthropicAndNonProfit?.tier || '-999'}
                onChange={(e) => {setFinancialFlowsState(3, 'tier', e.target.value)}}>
                {<option value="-999" key="">{t('newReport.successFactors.selectOnePlacehodler')}</option>}
                {localState.tiers.map((i) => <option value={i.key_} key={i.key_}>{i.value}</option>)}
                </select>
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.source')}</span>
                <textarea className="commonText big" required = {isRequiredFinFlow('sources')}
                maxLength={Utils.getFieldInfos(localState.reportFinancialFlowLayerIndex, 'sources')?.length || 9999}
                rows={5} placeholder={t('newReport.successFactors.sourcePlaceholder')} value={props.state.successFactorsTab?.financialFlowsTab.totalDomesticPhilanthropicAndNonProfit?.sources || ''} onChange={(e) => {setFinancialFlowsState(3, 'sources', e.target.value)}}/>
              </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          </div>
        </div>        

        { /* technicalPlanningTab */ }
        <div className={`oneTabContent technicalPlanningTab ${!localState.technicalPlanningTab.isActive && 'hidden'}`}>
          <div className="oneTabIntro">
            <div className="oneTabIntroNumAndTitle">
              <span className="oneTabIntroNumber">3</span>
              <span className="oneTabIntroTitle">{t('newReport.successFactors.technicalPlanningLabel')}</span>              
            </div>
            
            <p className="oneTabIntroText">{t('newReport.successFactors.technicalPlanningIntro')}</p>
          </div>
          
          <div className="itemsDiv row">
            <div className="itemsLeft">
              <div className="addItemDiv">
                <button className="actionBtn primary" onClick={addNewPlanningMethodology}>
                    <span>{t('newReport.successFactors.planningMethodologies')}</span>
                    <FontAwesomeIcon className="plusIcon" icon={ faPlus }/>
                </button>   
              </div>

              <div className="itemsList">

              {props.state.successFactorsTab?.technicalPlanningTab.planningMethodologies.map((p:ISuccessFactorsPlanningMethodology, i: number) =>
                <div className={`oneItem ${p.isActive ? 'active' : 'default'}`} key={i} onClick={ () => {selectPlanningMethodology(i)}}>
                  <span className="tabSpan" title={p.methodology}>{p.methodology}</span>
                  <FontAwesomeIcon className="tabIcon deleteIcon" icon={ faTrash } onClick={ (e) => { deletePlanningMethodology(e, i) }}/>
                </div>
              )}
              </div>
            </div>
            
            {getSelectedPlanningMethodology() &&
            <div className="itemsRight">
              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.whichToolsMethodologies')}</span>
                <p><input className="commonText medium" type="text" required = {isRequiredTechPlan('methodology')}
                maxLength={Utils.getFieldInfos(localState.reportTechnicalPlanningLayerIndex, 'methodology')?.length || 9999}
                placeholder={t('newReport.successFactors.whichToolsMethodologiesPlaceholder')} value={getSelectedPlanningMethodology()?.methodology || ''}  onChange={(e) => {updateSelectedPlanningMethodology('methodology', e.target.value)}}/> </p>
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.approachDescription')}</span>
                <textarea className="commonText big" required = {isRequiredTechPlan('description')}
                maxLength={Utils.getFieldInfos(localState.reportTechnicalPlanningLayerIndex, 'description')?.length || 9999}
                rows={5} placeholder={t('newReport.successFactors.approachDescriptionPlaceholder')} value={getSelectedPlanningMethodology()?.description || ''} onChange={(e) => {updateSelectedPlanningMethodology('description', e.target.value)}}/>
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.approachScale')}</span>
                <select className={`mediumText small ${isRequiredTechPlan('scale') ? "required" : ""}`} placeholder={t('newReport.successFactors.selectOnePlacehodler')}
                value={getSelectedPlanningMethodology()?.scale || '-999'} onChange={(e) => {updateSelectedPlanningMethodology('scale', e.target.value)}}>
                {<option value="-999" key="">{t('newReport.successFactors.selectOnePlacehodler')}</option>}
                {localState.planningApproachScaleFeatures.map((i) => <option value={i.key_} key={i.key_}>{i.value}</option>)}
                </select>
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.observationScale')}</span>
                <select className={`mediumText small ${isRequiredTechPlan('obs_scale') ? "required" : ""}`} placeholder={t('newReport.successFactors.selectOnePlacehodler')}
                value={getSelectedPlanningMethodology()?.obs_scale || '-999'} onChange={(e) => {updateSelectedPlanningMethodology('obs_scale', e.target.value)}}>
                {<option value="-999" key="">{t('newReport.successFactors.selectOnePlacehodler')}</option>}
                {localState.planningObservScalesFeatures.map((i) => <option value={i.key_} key={i.key_}>{i.value}</option>)}
                </select>
              </div>

              <div className={`row multiselectRow ${isRequiredTechPlan('landscape_status') ? "required" : ""}`}>
                <span className="titleSpan mediumText">{t('newReport.successFactors.landscapeStatusCondition')}</span>

                <div className="col-6">
                  <Select classNamePrefix="multiselect" placeholder={t('newReport.successFactors.selectAllThatAppliesPlaceholder')}
                  closeMenuOnSelect={true} isMulti
                  options={localState.planningStatusFeatures.map((i: IRefPlanningStatusFeature) => {return {label: i.value, value: i.key_}})}
                  onChange={handlePlanningStatusChange}
                  value={
                    localState.planningStatusFeatures.map((i: IRefPlanningStatusFeature) => {return {label: i.value, value: i.key_}})
                    .filter(option => (getSelectedPlanningMethodology()?.landscape_status || '').split(',').indexOf(option.value || '---') !== -1)
                  }/>
                </div>

                <div className="col-6">                
                  <input className="mediumText small right" required = {isRequiredTechPlan('explanation')}
                  maxLength={Utils.getFieldInfos(localState.reportTechnicalPlanningLayerIndex, 'explanation')?.length || 9999} placeholder={t('newReport.ifOtherSelected')}
                  type="text" disabled={!localState.otherStatusInputEnabled} value={getSelectedPlanningMethodology()?.explanation || ''} onChange={(e) => {updateSelectedPlanningMethodology('explanation', e.target.value)}}/>  
                </div>
              </div>

              <div className="row">
                  <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="isPriorityCbx" checked={getSelectedPlanningMethodology()?.species_adressed == 1} onChange={(e) => {updateSelectedPlanningMethodology('species_adressed', e.target.checked ? 1 : 0)}}/>
                      <label className="form-check-label mediumText" htmlFor="isPriorityCbx">{t('newReport.successFactors.isAPriority')}</label>
                  </div>
              </div>
              
              {getSelectedPlanningMethodology()?.species_adressed == 1 &&
              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.details')}</span>
                <textarea className="commonText big" required = {isRequiredTechPlan('details')}
                maxLength={Utils.getFieldInfos(localState.reportTechnicalPlanningLayerIndex, 'details')?.length || 9999}
                rows={5} placeholder={t('newReport.successFactors.detailsPlaceholder')} value={getSelectedPlanningMethodology()?.details || ''} onChange={(e) => {updateSelectedPlanningMethodology('details', e.target.value)}}/>
              </div>
              }

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.relevantCitations')}</span>
                <textarea className="commonText big" required = {isRequiredTechPlan('links')}
                maxLength={Utils.getFieldInfos(localState.reportTechnicalPlanningLayerIndex, 'links')?.length || 9999}
                rows={5} placeholder={t('newReport.successFactors.relevantCitationsPlaceholder')} value={getSelectedPlanningMethodology()?.links || ''} onChange={(e) => {updateSelectedPlanningMethodology('links', e.target.value)}}/>
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.relevantInformation')}</span>
                <textarea className="commonText big" required = {isRequiredTechPlan('information')}
                maxLength={Utils.getFieldInfos(localState.reportTechnicalPlanningLayerIndex, 'information')?.length || 9999}
                rows={5} placeholder={t('newReport.successFactors.relevantInformationPlaceholder')} value={getSelectedPlanningMethodology()?.information || ''} onChange={(e) => {updateSelectedPlanningMethodology('information', e.target.value)}}/>
              </div>
            </div>
            }
          </div>
        </div>

        { /* monitoringTab */ }
        <div className={`oneTabContent monitoringTab ${!localState.monitoringTab.isActive && 'hidden'}`}>
        <div className="oneTabIntro">
            <div className="oneTabIntroNumAndTitle">
              <span className="oneTabIntroNumber">4</span>
              <span className="oneTabIntroTitle">{t('newReport.successFactors.monitoringLabel')}</span>              
            </div>
            
            <p className="oneTabIntroText">{t('newReport.successFactors.monitoringIntro')}</p>
          </div>
          
          <div className="itemsDiv row">
            <div className="itemsLeft">
              <div className="addItemDiv">
                <button className="actionBtn primary" onClick={addNewMonitoringSystem}>
                    <span>{t('newReport.successFactors.monitoringSystems')}</span>
                    <FontAwesomeIcon className="plusIcon" icon={ faPlus }/>
                </button>   
              </div>

              <div className="itemsList">

              {props.state.successFactorsTab?.monitoringTab.monitoringSystems.map((p:ISuccessFactorsMonitoringSystem, i: number) =>
                <div className={`oneItem ${p.isActive ? 'active' : 'default'}`} key={i} onClick={ () => {selectMonitoringSystem(i)}}>
                  <span className="tabSpan" title={p.monitoring_system}>{p.monitoring_system}</span>
                  <FontAwesomeIcon className="tabIcon deleteIcon" icon={ faTrash } onClick={ (e) => { deleteMonitoringSystem(e, i) }}/>
                </div>
              )}
              </div>
            </div>
            
            {getSelectedMonitoringSystem() &&
            <div className="itemsRight">
              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.monitoringSystemName')}</span>
                <p><input className="commonText medium" type="text" required = {isRequiredMonitoring('monitoring_system')}
                maxLength={Utils.getFieldInfos(localState.reportTechnicalPlanningLayerIndex, 'monitoring_system')?.length || 9999}
                placeholder={t('newReport.successFactors.monitoringSystemNamePlaceholder')} value={getSelectedMonitoringSystem()?.monitoring_system || ''}  onChange={(e) => {updateSelectedMonitoringSystem('monitoring_system', e.target.value)}}/> </p>
              </div>

              <div className="row">
                  <span className="titleSpan mediumText">{t('newReport.successFactors.systemDescription')}</span>
                  <textarea className="commonText big" required = {isRequiredMonitoring('description')}
                  maxLength={Utils.getFieldInfos(localState.reportTechnicalPlanningLayerIndex, 'description')?.length || 9999}
                  rows={5} placeholder={t('newReport.successFactors.systemDescriptionPlaceholder')} value={getSelectedMonitoringSystem()?.description || ''} onChange={(e) => {updateSelectedMonitoringSystem('description', e.target.value)}}/>
              </div>

              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.systemLink')}</span>
                <p><input className="commonText medium" type="text" required = {isRequiredMonitoring('link')}
                maxLength={Utils.getFieldInfos(localState.reportTechnicalPlanningLayerIndex, 'link')?.length || 9999}
                placeholder={t('newReport.successFactors.systemLinkPlaceholder')} value={getSelectedMonitoringSystem()?.link || ''}  onChange={(e) => {updateSelectedMonitoringSystem('link', e.target.value)}}/> </p>
              </div>

              <div className="row">
                  <span className="titleSpan mediumText">{t('newReport.successFactors.managingInstitutions')}</span>
                  <textarea className="commonText big" required = {isRequiredMonitoring('managing_instit')}
                  maxLength={Utils.getFieldInfos(localState.reportTechnicalPlanningLayerIndex, 'managing_instit')?.length || 9999}
                  rows={5} placeholder={t('newReport.successFactors.managingInstitutionsPlaceholder')} value={getSelectedMonitoringSystem()?.managing_instit || ''} onChange={(e) => {updateSelectedMonitoringSystem('managing_instit', e.target.value)}}/>
              </div>
              
              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.geographicScale')}</span>
                <select className={`mediumText small ${isRequiredMonitoring('geo_scale') ? "required" : ""}`} placeholder={t('newReport.successFactors.selectOnePlacehodler')}
                value={getSelectedMonitoringSystem()?.geo_scale || '-999'} onChange={(e) => {updateSelectedMonitoringSystem('geo_scale', e.target.value)}}>
                {<option value="-999" key="">{t('newReport.successFactors.selectOnePlacehodler')}</option>}
                {localState.monitoringGeoScaleFeatures.map((i) => <option value={i.key_} key={i.key_}>{i.value}</option>)}
                </select>
              </div>

              <div className={`row multiselectRow ${isRequiredMonitoring('track_list') ? "required" : ""}`}>
                <span className="titleSpan mediumText">{t('newReport.successFactors.systemTrack')}</span>

                <div className="col-6">
                  <Select classNamePrefix="multiselect" placeholder={t('newReport.successFactors.selectAllThatAppliesPlaceholder')}
                  closeMenuOnSelect={true} isMulti
                  options={localState.monitoringTrackedElemFeatures.map((i: IRefMonitoringTrackedElemFeature) => {return {label: i.value, value: i.key_}})}
                  onChange={handleSystemTrackChange}
                  value={
                    localState.monitoringTrackedElemFeatures.map((i: IRefMonitoringTrackedElemFeature) => {return {label: i.value, value: i.key_}})
                    .filter(option => (getSelectedMonitoringSystem()?.track_list || '').split(',').indexOf(option.value || '---') !== -1)
                  }/>
                </div>

                <div className="col-6">                
                  <input className="mediumText small right" type="text" required = {isRequiredMonitoring('other')} placeholder={t('newReport.ifOtherSelected')}
                  disabled={!localState.otherSystemTrackEnabled} value={getSelectedMonitoringSystem()?.other} onChange={(e) => {updateSelectedMonitoringSystem('other', e.target.value)}}/>  
                </div>
              </div>

              <div className="row">
                  <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="isPriorityCbx" checked={getSelectedMonitoringSystem()?.mrv_system == 1} onChange={(e) => {updateSelectedMonitoringSystem('mrv_system', e.target.checked ? 1 : 0)}}/>
                      <label className="form-check-label mediumText" htmlFor="isPriorityCbx">{t('newReport.successFactors.isPartOfMRVSystem')}</label>
                  </div>
              </div>
              {getSelectedMonitoringSystem()?.mrv_system == 1 &&
              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.monitoringRelevantInfo')}</span>
                <textarea className="commonText big" required = {isRequiredMonitoring('information')}
                maxLength={Utils.getFieldInfos(localState.reportTechnicalPlanningLayerIndex, 'information')?.length || 9999}
                rows={5} placeholder={t('newReport.successFactors.monitoringRelevantInfoPlaceholder')} value={getSelectedMonitoringSystem()?.information || ''} onChange={(e) => {updateSelectedMonitoringSystem('information', e.target.value)}}/>
              </div>
              }
              <div className="row">
                <span className="titleSpan mediumText">{t('newReport.successFactors.monitoringRelevantCitations')}</span>
                <textarea className="commonText big" required = {isRequiredMonitoring('links')}
                maxLength={Utils.getFieldInfos(localState.reportTechnicalPlanningLayerIndex, 'links')?.length || 9999}
                rows={5} placeholder={t('newReport.successFactors.monitoringRelevantCitationsPlaceholder')} value={getSelectedMonitoringSystem()?.links || ''} onChange={(e) => {updateSelectedMonitoringSystem('links', e.target.value)}}/>
              </div>
            </div>
            }
          </div>
        </div>
      </div>

      <Modal show={showPolicyDialog} onHide={handlePolicyDialogClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t('newReport.successFactors.confirmDeletePolicyTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('newReport.successFactors.confirmDeletePolicyContent')}</Modal.Body>
        <Modal.Footer>
          <button className="dialogBtn secondary" onClick={handlePolicyDialogClose}>
          {t('newReport.successFactors.cofirmCancel')}
          </button>
          <button className="dialogBtn primary" onClick={handlePolicyConfirmDelete}>
          {t('newReport.successFactors.confirmDelete')}
          </button>
        </Modal.Footer>
      </Modal>
      
      <Modal show={showPlanningMethodologyDialog} onHide={handlePlanningMethodologyDialogClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t('newReport.successFactors.confirmDeletePlanningMethodTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('newReport.successFactors.confirmDeletePlanningMethodContent')}</Modal.Body>
        <Modal.Footer>
          <button className="dialogBtn secondary" onClick={handlePlanningMethodologyDialogClose}>
          {t('newReport.successFactors.cofirmCancel')}
          </button>
          <button className="dialogBtn primary" onClick={handlePlanningMethodologyConfirmDelete}>
          {t('newReport.successFactors.confirmDelete')}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showMonitoringSystemDialog} onHide={handleMonitoringSystemDialogClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t('newReport.successFactors.confirmDeleteMonitoringSystemTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('newReport.successFactors.confirmDeleteMonitoringSystemContent')}</Modal.Body>
        <Modal.Footer>
          <button className="dialogBtn secondary" onClick={handleMonitoringSystemDialogClose}>
          {t('newReport.successFactors.cofirmCancel')}
          </button>
          <button className="dialogBtn primary" onClick={handleMonitoringSystemConfirmDelete}>
          {t('newReport.successFactors.confirmDelete')}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SuccessFactors;

